import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { EmailIntegrationsContext } from "@redotech/redo-merchant-support-app/services/email-integrations";
import type { ReceivedAtEmailCondition as ReceivedAtEmailConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

export const ReceivedAtEmailCondition = memo(function ReceivedAtEmailCondition({
  condition,
  onChange,
}: {
  condition: ReceivedAtEmailConditionType;
  onChange: (updated: ReceivedAtEmailConditionType) => void;
}) {
  const [availableEmails, setAvailableEmails] = useState<string[]>([]);

  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);
  const team = useRequiredContext(TeamContext);

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }

    const emails = [
      ...(emailIntegrationsLoad.value.customIntegrations || []),
      ...(emailIntegrationsLoad.value.gmailIntegrations || []),
      ...(emailIntegrationsLoad.value.outlookIntegrations || []),
    ].map((integration) => integration.email);

    const defaultEmail = team.settings?.support?.defaultEmail;
    if (defaultEmail && !emails.includes(defaultEmail)) {
      emails.push(defaultEmail);
    }

    setAvailableEmails((oldEmails) => {
      const updatedEmails = [...oldEmails];
      for (const email of emails) {
        if (!updatedEmails.includes(email)) {
          updatedEmails.push(email);
        }
      }
      return updatedEmails;
    });
  }, [emailIntegrationsLoad.value, team]);

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const emailItems: RedoListItem<string>[] = useMemo(() => {
    const emails = availableEmails.length
      ? availableEmails
      : ["No email addresses configured"];
    return emails.map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [availableEmails]);

  const selectedItems = useMemo(() => {
    return (condition.emails || []).map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [condition.emails]);

  const displayText =
    (condition.emails?.length || 0) > 0
      ? condition.emails!.length === 1
        ? condition.emails![0]
        : `${condition.emails!.length} emails selected`
      : "Select email addresses";

  const isDisabled = availableEmails.length === 0;

  return (
    <Flex dir="column" gap="sm" w="full">
      <div style={{ fontSize: "12px", color: "#666" }}>Email Address</div>
      <RedoFilterDropdownAnchor
        className={styles.fullWidthButton}
        color={isDisabled ? "tertiary" : "primary"}
        onClick={() => {
          if (!isDisabled) {
            setDropdownOpen(!dropdownOpen);
          }
        }}
        ref={setDropdownButtonRef}
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={emailItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, emails: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
